import NuCalBaseView from "../NuCalBaseView.js";
import NuCalSingleCalendarView from "../NuCalSingleCalendarView.js";
import NuCalSelectView from "../NuCalSelectView.js";
import NuCalPrettyView from "../NuCalPrettyView.js";
import {focusLock,getFocusableElements,zeroPadNumber} from "../../utils/General.js";
import Events from "../../utils/Events.js";

/**
 * @extends NuCalBaseView
 * @param {Object} props properties.
 * @param {String} props.namespace The namespace in which this object should subscribe and publish events on.
 * @param {HTMLElement} props.view The HTML Element in which this object should render the output too.
 * @param {String} props.dateType Which date the object is dealing with. Can be "arrival", "departure" or "both". Default is "both".
 * @param {String} props.locale Language code. Default "en-US."
 * @param {String} props.UUID Unique Identifier. the default is randomly generated.
 * @param {Function(date)} props.toggleRenderTemplate A custom function that renders the button template.
 * @param {String} props.classPrefix A custom class prefix.
 * @param {String} props.inputId value for the id attribute for the hidden input value.
 * @param {String} props.inputName value for the name attribute for the hidden input value.
 * @param {Object} props.lexicon The translation objects.
 */

export default class NuCalSingleCalendarWidget extends NuCalBaseView {

	constructor(props){
		const superResult = super(props);
		if(superResult instanceof Error){
			return superResult;
		}
		this.inputId = props.inputId || this.UUID;
		this.inputName = props.inputName || this.UUID; 

		this.classPrefix = props.classPrefix || "cp-nuCalSingleCalendarWidget";

		this.view.innerHTML = `
                <div class="${this.classPrefix}">
                    <input type="hidden" name="${props.inputName}" id="${props.inputId}">
                    <button type="button" tabindex="0" class="${this.classPrefix}-toggle"></button>
                    <div class="${this.classPrefix}-mask"></div>
                    <div class="${this.classPrefix}-calContainer">
                        <div class="${this.classPrefix}-select"></div>
                        <div class="${this.classPrefix}-calendar"></div>
                        <button type="button" aria-label="close-calendar" class="${this.classPrefix}-close"></button>
                    </div>
                </div>
        `;

		const closeEventCallback = e => {
			e.preventDefault();
			this.isOpen = false;
		};

		this.view.addEventListener("keydown", e => {
			if(e.key == "Escape"){
				closeEventCallback(e);
			}
		});

		this.input = this.view.querySelector(`#${this.inputId}`);

		this.toggleButton = new NuCalPrettyView({
			"view": this.view.querySelector(`.${this.classPrefix}-toggle`),
			"namespace": this.namespace,
			"dateType": this.dateType,
			"locale": this.locale,
			"lexicon": this.lexicon,
			"renderTemplate": props.toggleRenderTemplate
		});

		this.toggleButton.view.addEventListener("click", e => {
			e.preventDefault();
			this.toggleOpen();
		});

		if( !(this.toggleButton.view instanceof HTMLButtonElement) ){
			this.toggleButton.view.addEventListener("keydown", e => {
				switch(e.key){
				case "Enter":
				case "Space":
				case " ":
					this.isOpen = true;
					break;
				}
			});
		}

		this.mask = this.view.querySelector(`.${this.classPrefix}-mask`);

		this.mask.addEventListener("click", closeEventCallback);

		this.closeButton = this.view.querySelector(`.${this.classPrefix}-close`);

		this.closeButton.addEventListener("click", closeEventCallback);

		this.selects = new NuCalSelectView({
			"view": this.view.querySelector(`.${this.classPrefix}-select`),
			"namespace": this.namespace,
			"dateType": this.dateType,
			"locale": this.locale,
			"lexicon": this.lexicon,
		});

		this.calendar = new NuCalSingleCalendarView({
			"view": this.view.querySelector(`.${this.classPrefix}-calendar`),
			"namespace": this.namespace,
			"dateType": this.dateType,
			"locale": this.locale,
			"lexicon": this.lexicon,
		});

		this.calContainer = this.view.querySelector(`.${this.classPrefix}-calContainer`);

		this._focusableElements = getFocusableElements(this.calContainer);

		this._focusableElements.forEach( el => {
			el.addEventListener("blur", event => {
				if(this.isOpen) focusLock(event,this.calContainer);
			});
		});

		this.isOpen = props.isOpen;

		this.closeOnCalendarSelect = props.closeOnCalendarSelect;
 
	}

	/**
     * Toggles whether the widget it open or closed.
     */

	toggleOpen(){
		this.isOpen = !this.isOpen;
	}

	/**
     * Set whether the widget is open or closed.
     * @type {Boolean}.
    */
	get isOpen(){
		return this._isOpen;
	}

	set isOpen(b){
		const wasOpen = this.isOpen;
		this._isOpen = (typeof b === "boolean")? b : false;
		if(this.isOpen){
			this.calContainer.classList.add(`${this.classPrefix}-calContainer--open`);
			this.calContainer.classList.remove(`${this.classPrefix}-calContainer--closed`);
			this.mask.classList.add(`${this.classPrefix}-mask--on`);
			this.mask.classList.remove(`${this.classPrefix}-mask--off`);
			this.toggleButton.view.classList.add(`${this.classPrefix}-toggle--on`);
			this.toggleButton.view.classList.remove(`${this.classPrefix}-toggle--off`);
			this._focusableElements[0].focus();
		}else{
			this.calContainer.classList.remove(`${this.classPrefix}-calContainer--open`);
			this.calContainer.classList.add(`${this.classPrefix}-calContainer--closed`);
			this.mask.classList.remove(`${this.classPrefix}-mask--on`);
			this.mask.classList.add(`${this.classPrefix}-mask--off`);
			this.toggleButton.view.classList.remove(`${this.classPrefix}-toggle--on`);
			this.toggleButton.view.classList.add(`${this.classPrefix}-toggle--off`);
			if(wasOpen)
				setTimeout( () => { this.toggleButton.view.focus(); }, 50);
		}
	}

	/**
     * Set whether to close the calendar to close when a date is selected.
     * @type {Boolean}.
    */
	get closeOnCalendarSelect(){
		return this._closeOnCalendarSelect;
	}

	set closeOnCalendarSelect(b){
		this._closeOnCalendarSelect = (typeof b === "boolean") ? b : false;
		this.calendar.onDateSelect = (this._closeOnCalendarSelect)? () => {
			this.isOpen = false;
		} : function(){};
	}


	subscribe(){
		this._subscriptions.push(Events.subscribe(this._events.datesUpdated,this.update.bind(this)));
	}

	update(props){
		let date;
		if(this.dateType === "arrival"){
			date = props.arrivalDate;
		}else{
			date = props.departureDate;
		}
		this.input.value = `${date.getFullYear()}-${zeroPadNumber(date.getMonth()+1,2)}-${zeroPadNumber(date.getDate(),2)}`;
	}
}